import React, { useEffect, useState } from 'react';
import auth from '../../firebase.init';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Navigate, useLocation } from 'react-router-dom';
import Loading from './Loading';
import { useSecureFetch } from '../../hooks/useSecureFetch';

const RequireAuth = ({ children }) => {
  const [user, loading, error] = useAuthState(auth);
  const location = useLocation();
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(false);
  const { secureFetch } = useSecureFetch(); 

  // Store auth state in sessionStorage when user logs in
  useEffect(() => {
    if (user) {
      sessionStorage.setItem('isAuthenticated', 'true');
      sessionStorage.setItem('userEmail', user.email);
    }
  }, [user]);

  // Check and store profile data
  useEffect(() => {
    const checkProfileCompleteness = async () => {
        if (user) {
            try {
                // Get cached profile data from sessionStorage first
                const cachedProfile = sessionStorage.getItem('userProfile');
                if (cachedProfile) {
                    const parsedProfile = JSON.parse(cachedProfile);
                    setUserProfile(parsedProfile);
                    checkProfileFields(parsedProfile);
                } else {
                    // Fetch from API if no cached data
                    const data = await secureFetch(`https://server.enjoywiki.com/flip-server/users?userEmail=${user.email}`);

                    if (data && data[0]) {
                        const fetchedUserProfile = data[0];

                        // Cache the profile data in sessionStorage
                        sessionStorage.setItem('userProfile', JSON.stringify(fetchedUserProfile));
                        setUserProfile(fetchedUserProfile);
                        checkProfileFields(fetchedUserProfile);
                    } else {
                        throw new Error('User profile not found');
                    }
                }
            } catch (error) {
                console.error('Error checking profile completeness:', error);
                // Store error state in sessionStorage
                sessionStorage.setItem('profileError', error.message);
            } finally {
                setProfileLoading(false);
            }
        }
    };

    checkProfileCompleteness();

    // Cleanup function to remove sensitive data when component unmounts
    return () => {
        if (!user) {
            sessionStorage.removeItem('userProfile');
            sessionStorage.removeItem('isAuthenticated');
            sessionStorage.removeItem('userEmail');
            sessionStorage.removeItem('profileError');
        }
    };
}, [user, secureFetch]);


  // Helper function to check profile completeness
  const checkProfileFields = (profile) => {
    if (!profile) return false;

    if (profile.userRole === 'Buyer') {
      const isComplete = Boolean(profile.userName);
      setProfileComplete(isComplete);
      sessionStorage.setItem('profileComplete', isComplete);
    } else {
      const isComplete = Boolean(
        profile.userName &&
        profile.address &&
        profile.city &&
        profile.country
      );
      setProfileComplete(isComplete);
      sessionStorage.setItem('profileComplete', isComplete);
    }
  };

  // Handle pending product purchase
  useEffect(() => {
    const handlePendingPurchase = () => {
      const pendingProductId = sessionStorage.getItem('pendingProductPurchase');
      const shouldRedirectToBuyPage = sessionStorage.getItem('redirectToBuyPage') === 'true';

      if (userProfile?.userRole === 'Buyer' && pendingProductId && shouldRedirectToBuyPage) {
        sessionStorage.removeItem('pendingProductPurchase');
        sessionStorage.removeItem('redirectToBuyPage');
        return <Navigate to={`/buy/${pendingProductId}`} />;
      }
    };

    handlePendingPurchase();
  }, [userProfile]);

  if (loading || (user && profileLoading)) {
    return <Loading />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!profileComplete) {
    return <Navigate to="/update-profile" state={{ from: location }} replace />;
  }

  return children;
};

export default RequireAuth;
